import { useState } from "react";
import type { ProductCardDTO } from "../../api/types.js";
import {
  customerStockLabel,
  isInstantPurchasable,
  unavailablePurchaseHint,
} from "../../lib/purchasable.js";
import { formatCustomerPayablePrice } from "../../lib/format.js";
import { messages } from "../../i18n/messages.js";
import { AppButton } from "../ui/AppButton.js";
import { ProductCard } from "./ProductCard.js";

interface ProductDetailViewProps {
  product: ProductCardDTO;
  relatedProducts: ProductCardDTO[];
  onBack: () => void;
  onAddToCart?: (productId: string, qty: number) => Promise<void>;
  onOpenRelated?: (product: ProductCardDTO) => void;
  addDisabled?: boolean;
}

export function ProductDetailView({
  product,
  relatedProducts,
  onBack,
  onAddToCart,
  onOpenRelated,
  addDisabled = false,
}: ProductDetailViewProps) {
  const stockLabel = customerStockLabel(product);
  const unavailableHint = unavailablePurchaseHint(product);
  const needsManager =
    !product.price_resolved ||
    product.purchasable === false ||
    product.availability === "preorder" ||
    product.availability === "unknown";
  const canAdd = isInstantPurchasable(product) && Boolean(onAddToCart);
  const sku = product.sku?.trim() || product.product_id;
  const barcode = product.barcode?.trim() || product.gtin?.trim() || null;
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  async function handleAdd() {
    if (!onAddToCart || !canAdd) {
      return;
    }
    setAdding(true);
    try {
      await onAddToCart(product.product_id, qty);
      setAdded(true);
    } finally {
      setAdding(false);
    }
  }

  async function copyValue(field: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 1500);
    } catch {
      /* clipboard optional */
    }
  }

  const purchasableRelated = relatedProducts.filter((item) => isInstantPurchasable(item));

  return (
    <section className="flex flex-1 flex-col pb-24">
      <div className="px-4 pt-3">
        <button
          type="button"
          onClick={onBack}
          className="min-h-11 text-sm font-medium text-[var(--tg-theme-link-color,#3390ec)]"
        >
          {messages.product.backToCatalog}
        </button>
      </div>

      <div className="overflow-hidden bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)]">
        {product.is_promotional && product.promo_label ? (
          <div className="px-4 pt-3">
            <span className="inline-flex rounded-full bg-orange-600 px-3 py-1 text-xs font-semibold text-white">
              {product.promo_label}
            </span>
          </div>
        ) : null}
        {product.image_url ? (
          <img src={product.image_url} alt="" className="aspect-square w-full object-cover" />
        ) : (
          <div className="flex aspect-square items-center justify-center text-sm opacity-40">
            {messages.common.noImage}
          </div>
        )}
      </div>

      <div className="space-y-3 px-4 py-4">
        <h2 className="text-xl font-semibold leading-tight">{product.title}</h2>
        <div className="rounded-[14px] border border-orange-200 bg-orange-50 px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-orange-800 opacity-80">
            {messages.product.skuLabel}
          </p>
          <p className="mt-1 text-base font-semibold">{sku}</p>
        </div>
        <p className="text-2xl font-semibold">{formatCustomerPayablePrice(product)}</p>
        {!product.price_resolved ? (
          <p className="text-sm text-amber-700">{messages.catalog.priceUnresolved}</p>
        ) : null}

        <dl className="grid grid-cols-1 gap-2 text-sm">
          <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
            <dt className="text-xs opacity-60">{messages.product.skuLabel}</dt>
            <dd className="flex items-center justify-between gap-2 font-medium">
              <span>{sku}</span>
              <button
                type="button"
                className="text-xs text-[var(--tg-theme-link-color,#3390ec)]"
                onClick={() => void copyValue("sku", sku)}
              >
                {copiedField === "sku" ? messages.product.copied : messages.product.copyCode}
              </button>
            </dd>
          </div>
          {barcode ? (
            <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
              <dt className="text-xs opacity-60">{messages.product.barcodeLabel}</dt>
              <dd className="flex items-center justify-between gap-2 font-medium">
                <span>{barcode}</span>
                <button
                  type="button"
                  className="text-xs text-[var(--tg-theme-link-color,#3390ec)]"
                  onClick={() => void copyValue("barcode", barcode)}
                >
                  {copiedField === "barcode" ? messages.product.copied : messages.product.copyCode}
                </button>
              </dd>
            </div>
          ) : null}
          {product.brand ? (
            <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
              <dt className="text-xs opacity-60">{messages.product.brand}</dt>
              <dd className="font-medium">{product.brand}</dd>
            </div>
          ) : null}
          {product.country ? (
            <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
              <dt className="text-xs opacity-60">{messages.product.country}</dt>
              <dd className="font-medium">{product.country}</dd>
            </div>
          ) : null}
          {product.weight_or_volume ? (
            <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
              <dt className="text-xs opacity-60">{messages.product.weightOrVolume}</dt>
              <dd className="font-medium">{product.weight_or_volume}</dd>
            </div>
          ) : null}
          <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
            <dt className="text-xs opacity-60">{messages.product.category}</dt>
            <dd className="font-medium">{product.category}</dd>
          </div>
          <div className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
            <dt className="text-xs opacity-60">{messages.product.stock}</dt>
            <dd className="font-medium">{stockLabel}</dd>
          </div>
        </dl>

        {unavailableHint ? (
          <p className="rounded-[14px] bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-2 text-sm font-medium opacity-80">
            {unavailableHint}
          </p>
        ) : null}

        {needsManager ? (
          <div className="rounded-[16px] border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">{messages.product.managerHelpTitle}</p>
            <p className="mt-1 opacity-80">{messages.product.managerHelpBody}</p>
          </div>
        ) : null}

        {product.short_description ? (
          <p className="text-sm leading-relaxed opacity-75">{product.short_description}</p>
        ) : null}

        {canAdd ? (
          <div className="flex items-center justify-between rounded-[16px] border border-black/10 px-3 py-2">
            <span className="text-sm font-medium">{messages.product.quantity}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={qty <= 1}
                onClick={() => setQty((value) => Math.max(1, value - 1))}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 disabled:opacity-40"
              >
                −
              </button>
              <span className="min-w-6 text-center text-sm font-semibold">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((value) => Math.min(99, value + 1))}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10"
              >
                +
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {purchasableRelated.length > 0 ? (
        <div className="px-4 pb-4">
          <h3 className="mb-2 text-sm font-semibold">{messages.product.relatedTitle}</h3>
          <div className="grid grid-cols-2 gap-3">
            {purchasableRelated.slice(0, 4).map((item) => (
              <ProductCard
                key={item.id}
                product={item}
                layout="grid"
                onSelect={(selected) => onOpenRelated?.(selected)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {canAdd ? (
        <div className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] left-1/2 z-10 w-full max-w-[520px] -translate-x-1/2 px-4">
          <AppButton
            className="w-full shadow-lg"
            disabled={addDisabled || adding}
            onClick={() => void handleAdd()}
          >
            {added ? messages.catalog.added : adding ? messages.catalog.adding : messages.product.stickyAdd}
          </AppButton>
        </div>
      ) : null}
    </section>
  );
}
